import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { profileService } from '../../services/profileService';
import { settingsService } from '../../services/settingsService';
import { useClient } from '../../context/ClientContext';
import { useNavigate } from 'react-router-dom';
import CreateBusinessModal from '../../components/layout/CreateBusinessModal';

const { 
  FiUser, FiMail, FiLock, FiBell, FiBriefcase,
  FiSave, FiCheckCircle, FiAlertCircle, FiEye, FiEyeOff,
  FiChevronRight, FiStar, FiUsers, FiSettings,
  FiLogOut, FiPlus, FiLoader, FiChevronDown,
  FiArrowLeft, FiArchive
} = FiIcons;

const TABS = [
  { id: 'personal', label: 'Personal Information', icon: FiUser },
  { id: 'emails', label: 'Emails', icon: FiMail },
  { id: 'security', label: 'Password & Security', icon: FiLock },
  { id: 'notifications', label: 'Notifications', icon: FiBell },
  { id: 'businesses', label: 'Businesses', icon: FiBriefcase }
];

export default function UserProfile() {
  const navigate = useNavigate();
  const { clients, activeClient, refreshProfile, switchClient } = useClient();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessForm, setBusinessForm] = useState(null);
  
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'Member',
    phone: '',
    preferences: {
      country: 'United States',
      city: '',
      dob: '',
      notifications: true
    }
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    show: false
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getProfile();
      setProfile({
        first_name: data.first_name || data.full_name?.split(' ')[0] || '',
        last_name: data.last_name || data.full_name?.split(' ').slice(1).join(' ') || '',
        email: data.email || '',
        role: data.role || 'Member',
        phone: data.phone || '',
        preferences: {
          country: data.preferences?.country || 'United States',
          city: data.preferences?.city || '',
          dob: data.preferences?.dob || '',
          notifications: data.preferences?.notifications ?? true
        }
      });
    } catch (err) {
      console.error('Profile load error:', err);
      setError("Unable to load profile. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      await profileService.updateProfile(profile);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    try {
      setSaving(true);
      await profileService.updatePassword(passwordForm.newPassword);
      setSuccess(true);
      setPasswordForm({ newPassword: '', confirmPassword: '', show: false });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to update password.");
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (clientId) => {
    if (activeClient?.id !== clientId) {
      await switchClient(clientId);
    }
  };

  const handleLeaveBusiness = async (clientId) => {
    if (window.confirm("Are you sure you want to leave this business? You will lose access immediately.")) {
      try {
        await settingsService.leaveBusiness(clientId);
        await refreshProfile();
      } catch (err) {
        setError("Failed to leave business.");
      }
    }
  };

  const handleEditBusiness = async (client) => {
    try {
      setLoading(true);
      const fullClient = await settingsService.getAccountById(client.id);
      
      let addressObj = { line1: '', line2: '', city: '', state: '', zip: '', fax: '', mobile: '', tollFree: '', website: '', timezone: 'America/New_York' };
      try {
        if (fullClient.address && fullClient.address.startsWith('{')) {
          addressObj = { ...addressObj, ...JSON.parse(fullClient.address) };
        } else if (fullClient.address) {
          addressObj.line1 = fullClient.address;
        }
      } catch(e) {
        console.error('Failed to parse address:', e);
      }

      setBusinessForm({
        ...fullClient,
        addressObj
      });
      setEditingBusiness(client.id);
      setError(null);
    } catch (err) {
      setError("Failed to load business details.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBusiness = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const addressString = JSON.stringify(businessForm.addressObj);
      const payload = {
        name: businessForm.name,
        address: addressString,
        country: businessForm.country,
        phone: businessForm.phone
      };
      
      await settingsService.updateAccount(editingBusiness, payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      await refreshProfile();
      setEditingBusiness(null);
    } catch (err) {
       setError(err.message || 'Failed to update business');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveBusiness = async () => {
    if (window.confirm(`Are you sure you want to archive ${businessForm.name}? Archived businesses are hidden from normal views.`)) {
      try {
        setSaving(true);
        setError(null);
        await settingsService.updateAccount(editingBusiness, { status: 'archived' });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        await refreshProfile();
        setEditingBusiness(null);
      } catch (err) {
        setError(err.message || 'Failed to archive business');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading && !editingBusiness) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-medium text-sm">Loading Profile...</p>
      </div>
    );
  }

  const activeBusinesses = clients.filter(c => c.status !== 'archived' && c.status !== 'Archived');
  const yourBusinesses = activeBusinesses.filter(c => c.role === 'Super Admin' || c.role === 'Owner');
  const collaboratorBusinesses = activeBusinesses.filter(c => c.role !== 'Super Admin' && c.role !== 'Owner');

  return (
    <div className="max-w-[1200px] mx-auto py-8 px-4 sm:px-6 flex flex-col md:flex-row gap-12">
      
      <div className="w-full md:w-72 shrink-0">
        <h1 className="text-2xl font-bold text-slate-900 mb-8 px-2">Profile Settings</h1>
        <nav className="space-y-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setEditingBusiness(null);
                setError(null);
                setSuccess(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all group ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <SafeIcon icon={tab.icon} className={`text-lg ${activeTab === tab.id ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
              {tab.label}
              {activeTab === tab.id && <FiChevronRight className="ml-auto" />}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 bg-white">
        
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-3 text-rose-600 text-sm font-medium">
            <SafeIcon icon={FiAlertCircle} className="text-lg" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-600 text-sm font-medium">
            <SafeIcon icon={FiCheckCircle} className="text-lg" />
            Changes saved successfully!
          </div>
        )}

        {activeTab === 'personal' && (
          <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Personal Information</h2>
              <p className="text-slate-500 text-[15px] leading-relaxed">
                Provide as much or as little information as you'd like. We will never share or sell individual personal information.
              </p>
            </div>

            <div className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">First Name</label>
                <div className="sm:col-span-2">
                  <input 
                    type="text"
                    value={profile.first_name}
                    onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">Last Name</label>
                <div className="sm:col-span-2">
                  <input 
                    type="text"
                    value={profile.last_name}
                    onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">Role</label>
                <div className="sm:col-span-2">
                  <div className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700">
                    {profile.role}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Your role is managed by your business administrator.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">Country</label>
                <div className="sm:col-span-2 relative">
                  <select 
                    value={profile.preferences.country}
                    onChange={(e) => setProfile({...profile, preferences: {...profile.preferences, country: e.target.value}})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none appearance-none"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <SafeIcon icon={FiChevronDown} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">City</label>
                <div className="sm:col-span-2">
                  <input 
                    type="text"
                    value={profile.preferences.city}
                    onChange={(e) => setProfile({...profile, preferences: {...profile.preferences, city: e.target.value}})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="pt-6 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-all disabled:opacity-50 active:scale-95 shadow-lg shadow-blue-600/20"
                >
                  <SafeIcon icon={saving ? FiLoader : FiSave} className={saving ? "animate-spin" : ""} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'emails' && (
          <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
            <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Emails</h2>
            <p className="text-slate-500 mb-8 text-[15px]">Manage the email addresses associated with your account.</p>
            
            <div className="p-6 border border-slate-100 rounded-2xl bg-slate-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="text-xl" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{profile.email}</p>
                    <p className="text-xs font-semibold text-emerald-600 mt-0.5 flex items-center gap-1">
                      <SafeIcon icon={FiCheckCircle} /> Primary & Verified
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
            <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Password & Security</h2>
            <p className="text-slate-500 mb-10 text-[15px]">Update your password to keep your account secure.</p>
            
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">New Password</label>
                <div className="sm:col-span-2 relative">
                  <input 
                    type={passwordForm.show ? "text" : "password"}
                    required
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                  />
                  <button 
                    type="button"
                    onClick={() => setPasswordForm({...passwordForm, show: !passwordForm.show})}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <SafeIcon icon={passwordForm.show ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
                <label className="text-sm font-bold text-slate-700">Confirm Password</label>
                <div className="sm:col-span-2">
                  <input 
                    type={passwordForm.show ? "text" : "password"}
                    required
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all disabled:opacity-50 active:scale-95 shadow-lg shadow-slate-900/10"
                >
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
            <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Notifications</h2>
            <p className="text-slate-500 mb-8 text-[15px]">Control how you receive updates and financial alerts.</p>
            
            <div className="p-6 border border-slate-100 rounded-2xl bg-slate-50/50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiBell} className="text-xl" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Email Notifications</p>
                  <p className="text-xs text-slate-500 mt-0.5">Receive reports, alerts, and account activity updates.</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={profile.preferences.notifications}
                  onChange={(e) => {
                    setProfile({...profile, preferences: {...profile.preferences, notifications: e.target.checked}});
                    handleSave();
                  }}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        )}

        {activeTab === 'businesses' && !editingBusiness && (
          <div className="max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-300 pb-12">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">Your Businesses</h2>
                <p className="text-slate-500 text-[15px]">Manage the businesses you own or collaborate with.</p>
              </div>
              <button 
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-95"
              >
                <SafeIcon icon={FiPlus} />
                Create a business
              </button>
            </div>
            
            <div className="space-y-12">
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiBriefcase} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Owned Businesses</h3>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {yourBusinesses.map((client) => (
                    <div 
                      key={client.id} 
                      className={`group flex flex-col p-5 rounded-2xl border transition-all cursor-pointer ${
                        activeClient?.id === client.id 
                          ? 'border-blue-200 bg-blue-50/30' 
                          : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50/50'
                      }`}
                      onClick={() => handleEditBusiness(client)}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                            activeClient?.id === client.id ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {client.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold text-slate-900">{client.name}</h4>
                              {activeClient?.id === client.id && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full uppercase tracking-wider">Active</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                              <span className="flex items-center gap-1"><SafeIcon icon={FiUser} className="text-[10px]" /> {client.role}</span>
                              {client.industry && <span className="flex items-center gap-1"><SafeIcon icon={FiBriefcase} className="text-[10px]" /> {client.industry}</span>}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 mr-2">
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleSetDefault(client.id); }}
                              className={`p-2 rounded-lg transition-all ${activeClient?.id === client.id ? 'text-blue-600 bg-blue-100/50' : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'}`}
                              title="Set as Default"
                            >
                              <SafeIcon icon={FiStar} className={activeClient?.id === client.id ? "fill-current" : ""} />
                            </button>
                          </div>
                          
                          <button 
                            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg transition-all"
                            onClick={(e) => { e.stopPropagation(); handleEditBusiness(client); }}
                          >
                            <SafeIcon icon={FiChevronRight} className="text-lg" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {yourBusinesses.length === 0 && (
                    <div className="py-12 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center text-center">
                      <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mb-3">
                        <SafeIcon icon={FiBriefcase} className="text-xl" />
                      </div>
                      <p className="text-sm font-bold text-slate-900">No active businesses found</p>
                      <p className="text-xs text-slate-500 mt-1">Create your first business ledger to get started.</p>
                    </div>
                  )}
                </div>
              </section>

              {collaboratorBusinesses.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiUsers} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">Collaborator Businesses</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3">
                    {collaboratorBusinesses.map((client) => (
                      <div 
                        key={client.id} 
                        className="group flex flex-col p-5 rounded-2xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-all cursor-pointer"
                        onClick={() => handleEditBusiness(client)}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center font-bold text-lg">
                              {client.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-900">{client.name}</h4>
                              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase">{client.role}</span>
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-3 mr-2">
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleLeaveBusiness(client.id); }}
                                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                title="Leave Business"
                              >
                                <SafeIcon icon={FiLogOut} />
                              </button>
                            </div>
                            <button 
                              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg transition-all"
                              onClick={(e) => { e.stopPropagation(); handleEditBusiness(client); }}
                            >
                              <SafeIcon icon={FiChevronRight} className="text-lg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
            
            <CreateBusinessModal 
              isOpen={isCreateModalOpen} 
              onClose={() => setIsCreateModalOpen(false)} 
            />
          </div>
        )}

        {activeTab === 'businesses' && editingBusiness && businessForm && (
          <div className="max-w-4xl animate-in fade-in slide-in-from-right-4 duration-300 pb-12">
            
            {loading && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            )}

            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setEditingBusiness(null)} 
                  className="p-2 -ml-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiArrowLeft} className="text-xl" />
                </button>
                <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Edit {businessForm.name}</h2>
              </div>
              <button 
                onClick={handleSaveBusiness}
                disabled={saving || loading}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <SafeIcon icon={FiLoader} className="animate-spin" />}
                {saving ? 'Saving...' : 'Save all changes'}
              </button>
            </div>

            <div className="space-y-10">
              
              <section>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Business info your customer sees</h3>
                <p className="text-sm text-slate-500 mb-6">
                  This is the information shown on all existing and future invoices and estimates. 
                </p>
                
                <div className="max-w-xl">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Business name <span className="text-rose-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    value={businessForm.name || ''}
                    onChange={(e) => setBusinessForm({...businessForm, name: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                  />
                </div>
              </section>

              <section>
                <h3 className="text-lg font-bold text-slate-900 mb-6">Address</h3>
                
                <div className="max-w-2xl space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Address line 1</label>
                    <input 
                      type="text" 
                      value={businessForm.addressObj.line1}
                      onChange={(e) => setBusinessForm({...businessForm, addressObj: {...businessForm.addressObj, line1: e.target.value}})}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Address line 2</label>
                    <input 
                      type="text" 
                      value={businessForm.addressObj.line2}
                      onChange={(e) => setBusinessForm({...businessForm, addressObj: {...businessForm.addressObj, line2: e.target.value}})}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">City</label>
                      <input 
                        type="text" 
                        value={businessForm.addressObj.city}
                        onChange={(e) => setBusinessForm({...businessForm, addressObj: {...businessForm.addressObj, city: e.target.value}})}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Postal/Zip code</label>
                      <input 
                        type="text" 
                        value={businessForm.addressObj.zip}
                        onChange={(e) => setBusinessForm({...businessForm, addressObj: {...businessForm.addressObj, zip: e.target.value}})}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Country <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <select 
                          value={businessForm.country || 'United States'}
                          onChange={(e) => setBusinessForm({...businessForm, country: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none appearance-none"
                        >
                          <option value="United States">United States</option>
                          <option value="Canada">Canada</option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="Australia">Australia</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                          <SafeIcon icon={FiChevronDown} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Province/State</label>
                      <input 
                        type="text" 
                        placeholder="Select..."
                        value={businessForm.addressObj.state}
                        onChange={(e) => setBusinessForm({...businessForm, addressObj: {...businessForm.addressObj, state: e.target.value}})}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="pt-8 border-t border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Contact Information</h3>
                
                <div className="max-w-2xl space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Phone</label>
                      <input 
                        type="text" 
                        value={businessForm.phone || ''}
                        onChange={(e) => setBusinessForm({...businessForm, phone: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Website</label>
                      <input 
                        type="text" 
                        value={businessForm.addressObj.website || ''}
                        onChange={(e) => setBusinessForm({...businessForm, addressObj: {...businessForm.addressObj, website: e.target.value}})}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="pt-8 mt-8 border-t border-rose-100">
                <h3 className="text-lg font-bold text-rose-600 mb-2">Danger Zone</h3>
                <p className="text-sm text-slate-500 mb-6">
                  Irreversible and destructive actions for this business.
                </p>
                
                <div className="p-6 border border-rose-200 bg-rose-50/50 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Archive this business</h4>
                    <p className="text-xs text-slate-500 mt-1">Once archived, this business will be hidden from the active list.</p>
                  </div>
                  <button 
                    onClick={handleArchiveBusiness}
                    disabled={saving || loading}
                    className="flex items-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl font-bold text-sm hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 active:scale-95 disabled:opacity-50 shrink-0"
                  >
                    <SafeIcon icon={FiArchive} />
                    {saving ? 'Archiving...' : 'Archive Business'}
                  </button>
                </div>
              </section>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}